import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Coins,
  Copy,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Settings as SettingsIcon,
  Shield,
  ShieldOff,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toast';
import {
  tsToDate,
  useAdminSetUserProfile,
  useAdminUsers,
  useAdminVerificationDocs,
  useCreateAdminUser,
  useDeleteAdminUser,
  useSetUserDisabled,
  useSetUserRole,
  useUpdateMembership,
  type AdminUserRow,
  type CreateAdminUserInput,
} from '../../hooks/useAdminUsers';
import { useMembershipPlans } from '../../hooks/useMembershipPlans';
import { useUser } from '../../auth/AuthProvider';
import { HeaderActions } from '../../components/HeaderActions';
import { cn } from '../../lib/utils';

export function AdminUsersPage() {
  const users = useAdminUsers();
  const [editing, setEditing] = React.useState<AdminUserRow | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    if (!users.data) return [];
    if (!search.trim()) return users.data;
    const q = search.toLowerCase();
    return users.data.filter((u) => {
      const haystack = [u.email, u.displayName, u.firstName, u.lastName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [users.data, search]);

  return (
    <div className="space-y-6">
      <HeaderActions>
        <Input
          placeholder="Suchen (Name oder E-Mail) …"
          className="h-8 w-56"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Benutzer anlegen
        </Button>
      </HeaderActions>

      <Card>
        <CardHeader>
          <CardTitle>Alle Benutzer</CardTitle>
          <CardDescription>
            {users.isLoading
              ? 'Lade …'
              : `${filtered.length} von ${users.data?.length ?? 0} Konten`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Benutzer …
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Keine Benutzer gefunden.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2">Benutzer</th>
                    <th className="px-2 py-2">Rolle</th>
                    <th className="px-2 py-2">Tier</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Gültig bis</th>
                    <th className="px-2 py-2">Letzter Login</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <UserRow key={u.uid} u={u} onEdit={() => setEditing(u)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditMembershipDialog
          row={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {creating && <CreateUserDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

// ── Create-user dialog ───────────────────────────────────────────────────

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateAdminUser();
  const plans = useMembershipPlans();
  const { toast } = useToast();

  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [role, setRole] = React.useState<'admin' | 'user'>('user');
  const [tier, setTier] = React.useState<'trial' | 'free' | 'pro' | 'enterprise' | 'ultimate'>('trial');
  const [status, setStatus] = React.useState<'active' | 'paused' | 'expired' | 'cancelled'>('active');
  const [planId, setPlanId] = React.useState<string>('');
  const [validUntil, setValidUntil] = React.useState<string>(
    () => toDateInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
  );
  const [unlimited, setUnlimited] = React.useState(false);
  const [notes, setNotes] = React.useState('');

  // Password handling: invite-mode (default) generates a reset link the
  // admin can email. Manual mode lets the admin pick the password now.
  const [mode, setMode] = React.useState<'invite' | 'manual'>('invite');
  const [password, setPassword] = React.useState('');

  const [inviteLink, setInviteLink] = React.useState<string | null>(null);
  const [createdUid, setCreatedUid] = React.useState<string | null>(null);

  const activePlans = (plans.data ?? []).filter((p) => p.isActive);

  function applyPlan(newPlanId: string) {
    setPlanId(newPlanId);
    if (!newPlanId) return;
    const plan = activePlans.find((p) => p.id === newPlanId);
    if (!plan) return;
    setTier(plan.tier);
    if (plan.durationDays != null) {
      const until = new Date();
      until.setDate(until.getDate() + plan.durationDays);
      setValidUntil(toDateInput(until));
      setUnlimited(false);
    } else {
      setUnlimited(true);
      setValidUntil('');
    }
  }

  async function onSubmit() {
    if (!email.trim()) {
      toast({ title: 'E-Mail-Adresse fehlt', variant: 'destructive' });
      return;
    }
    const payload: CreateAdminUserInput = {
      email: email.trim(),
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      displayName: displayName.trim() || null,
      role,
      membership: {
        tier,
        status,
        validFromMs: Date.now(),
        validUntilMs: unlimited
          ? null
          : validUntil
            ? new Date(validUntil).getTime()
            : null,
        notes: notes.trim() || null,
        planId: planId || null,
      },
      generateInviteLink: mode === 'invite',
    };
    if (mode === 'manual') {
      if (!password || password.length < 8) {
        toast({ title: 'Passwort muss min. 8 Zeichen haben', variant: 'destructive' });
        return;
      }
      payload.password = password;
    }
    try {
      const r = await create.mutateAsync(payload);
      setCreatedUid(r.uid);
      setInviteLink(r.inviteLink);
      toast({
        title: 'Benutzer angelegt',
        description: r.inviteLink
          ? 'Invite-Link unten anzeigen + an den Benutzer schicken.'
          : 'Login mit der von dir gesetzten Mail/Passwort möglich.',
      });
    } catch (err) {
      toast({
        title: 'Anlegen fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  function copyInvite() {
    if (!inviteLink) return;
    void navigator.clipboard.writeText(inviteLink);
    toast({ title: 'Invite-Link kopiert' });
  }

  // Post-success view: just show the invite link + close button.
  if (createdUid) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Benutzer angelegt</DialogTitle>
            <DialogDescription>{email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {inviteLink ? (
              <div className="space-y-2">
                <Label>Invite-Link (an den Benutzer schicken)</Label>
                <div className="flex gap-2">
                  <Input readOnly value={inviteLink} className="font-mono text-xs" />
                  <Button size="sm" variant="outline" onClick={copyInvite}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" /> Kopieren
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Über diesen Link setzt der Benutzer ein eigenes Passwort und
                  loggt sich danach unter <code>cp-3.web.app/signin</code> ein.
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-300">
                Login mit E-Mail + manuell gesetztem Passwort möglich.
              </div>
            )}
            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              UID: <code className="font-mono">{createdUid}</code> · Default-PhoneSettings
              wurden initialisiert. Weitere Einstellungen (Provider, KI-Modell, Stimme,
              Kalender, Tickets) findest du jetzt unter{' '}
              <Link
                to={`/admin/users/${createdUid}/settings/provider`}
                className="text-primary hover:underline"
              >
                Einstellungen verwalten
              </Link>
              .
            </div>
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Fertig</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
          <DialogDescription>
            Legt sofort einen Auth-Account + Firestore-Doc + Default-PhoneSettings an.
            Detail-Konfiguration (Anbieter, Stimme, Kalender) folgt anschließend
            unter „Einstellungen verwalten".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-Mail*</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="kunde@firma.de"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rolle</Label>
              <Select value={role} onValueChange={(v) => setRole(v as 'admin' | 'user')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">Vorname</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Nachname</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="displayName">Anzeigename (optional)</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={
                [firstName, lastName].filter(Boolean).join(' ') ||
                'Wird aus Vor + Nachname generiert'
              }
              autoComplete="off"
            />
          </div>

          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Login-Modus
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <ModeRadio
                label="Invite-Link"
                description="Benutzer setzt sein eigenes Passwort über einen Reset-Link, den du ihm schickst."
                active={mode === 'invite'}
                onClick={() => setMode('invite')}
              />
              <ModeRadio
                label="Passwort jetzt setzen"
                description="Du legst das Passwort fest und teilst es dem Benutzer manuell mit."
                active={mode === 'manual'}
                onClick={() => setMode('manual')}
              />
            </div>
            {mode === 'manual' && (
              <div className="space-y-1.5 pt-1">
                <Label htmlFor="password">Initial-Passwort (min. 8 Zeichen)</Label>
                <Input
                  id="password"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="z.B. eines aus deinem Passwort-Manager"
                  autoComplete="off"
                />
              </div>
            )}
          </div>

          {activePlans.length > 0 && (
            <div className="space-y-1.5">
              <Label>Plan zuweisen (optional)</Label>
              <Select
                value={planId || '__none__'}
                onValueChange={(v) => applyPlan(v === '__none__' ? '' : v)}
              >
                <SelectTrigger><SelectValue placeholder="Manuell konfigurieren" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Manuell konfigurieren</SelectItem>
                  {activePlans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {p.tier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Tier</Label>
              <Select value={tier} onValueChange={(v) => setTier(v as typeof tier)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="ultimate">Ultimate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="paused">Pausiert</SelectItem>
                  <SelectItem value="expired">Abgelaufen</SelectItem>
                  <SelectItem value="cancelled">Gekündigt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="validUntil">Gültig bis</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id="validUntil"
                type="date"
                value={validUntil}
                onChange={(e) => {
                  setValidUntil(e.target.value);
                  if (e.target.value) setUnlimited(false);
                }}
                disabled={unlimited}
                className="w-44"
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={unlimited}
                  onChange={(e) => setUnlimited(e.target.checked)}
                />
                Unbegrenzt
              </label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notizen (optional)</Label>
            <Input
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="z.B. Vertragsnummer, Sondertarif, …"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button onClick={() => void onSubmit()} disabled={create.isPending || !email.trim()}>
            {create.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModeRadio({
  label,
  description,
  active,
  onClick,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border p-3 text-left transition-colors',
        active
          ? 'border-primary bg-primary/10'
          : 'border-border hover:border-primary/40',
      )}
    >
      <div className="text-sm font-medium">{label}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
    </button>
  );
}

function UserRow({ u, onEdit }: { u: AdminUserRow; onEdit: () => void }) {
  const { user: me } = useUser();
  const setRole = useSetUserRole();
  const setDisabled = useSetUserDisabled();
  const remove = useDeleteAdminUser();
  const { toast } = useToast();
  const validUntil = tsToDate(u.membership?.validUntil ?? null);
  const lastSignIn = tsToDate(u.lastSignInAt ?? u.authLastSignIn);
  const expired = validUntil != null && validUntil < new Date();

  const isMe = me?.uid === u.uid;

  async function toggleAdmin() {
    try {
      await setRole.mutateAsync({ uid: u.uid, role: u.role === 'admin' ? 'user' : 'admin' });
      toast({ title: u.role === 'admin' ? 'Admin-Rolle entfernt' : 'Zum Admin gemacht' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }
  async function toggleDisabled() {
    try {
      await setDisabled.mutateAsync({ uid: u.uid, disabled: !u.disabled });
      toast({ title: u.disabled ? 'Konto entsperrt' : 'Konto gesperrt' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }
  async function onDelete() {
    if (!confirm(`Konto ${u.email} wirklich endgültig löschen?`)) return;
    try {
      await remove.mutateAsync(u.uid);
      toast({ title: 'Konto gelöscht' });
    } catch (err) {
      toast({ title: 'Löschung fehlgeschlagen', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <tr className={cn('border-b border-border/50 last:border-0', u.disabled && 'opacity-60')}>
      <td className="px-2 py-3">
        <Link
          to={`/admin/users/${u.uid}`}
          className="flex flex-col hover:underline"
          title="Einstellungen verwalten"
        >
          <span className="font-medium">{prettyName(u)}</span>
          <span className="text-xs text-muted-foreground">{u.email}</span>
        </Link>
      </td>
      <td className="px-2 py-3">
        <Badge variant={u.role === 'admin' ? 'amber' : 'muted'}>{u.role}</Badge>
      </td>
      <td className="px-2 py-3">
        <Badge variant="primary">{u.membership?.tier ?? '—'}</Badge>
      </td>
      <td className="px-2 py-3">
        <Badge variant={statusVariant(u.membership?.status)}>{u.membership?.status ?? '—'}</Badge>
      </td>
      <td className={cn('px-2 py-3 text-xs', expired && 'text-destructive')}>
        {validUntil ? validUntil.toLocaleDateString('de-DE') : 'unbegrenzt'}
      </td>
      <td className="px-2 py-3 text-xs text-muted-foreground">
        {lastSignIn ? lastSignIn.toLocaleString('de-DE') : '—'}
      </td>
      <td className="px-2 py-3 text-right">
        <div className="flex justify-end gap-1">
          <Button
            asChild
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            title="Nutzung & Kosten ansehen"
          >
            <Link to={`/admin/users/${u.uid}`}>
              <Coins className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <Button
            asChild
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            title="Einstellungen verwalten"
          >
            <Link to={`/admin/users/${u.uid}/settings/provider`}>
              <SettingsIcon className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} title="Mitgliedschaft bearbeiten">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={toggleAdmin}
            disabled={isMe || setRole.isPending}
            title={u.role === 'admin' ? 'Admin-Rolle entfernen' : 'Zum Admin machen'}
          >
            {u.role === 'admin' ? <ShieldOff className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={toggleDisabled}
            disabled={isMe || setDisabled.isPending}
            title={u.disabled ? 'Entsperren' : 'Sperren'}
          >
            {u.disabled ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onDelete}
            disabled={isMe || remove.isPending}
            title="Konto löschen"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function Badge({
  children,
  variant = 'muted',
}: {
  children: React.ReactNode;
  variant?: 'muted' | 'primary' | 'amber' | 'green' | 'red';
}) {
  const styles: Record<typeof variant, string> = {
    muted: 'bg-muted text-muted-foreground',
    primary: 'bg-primary/15 text-primary',
    amber: 'bg-amber-500/15 text-amber-400',
    green: 'bg-emerald-500/15 text-emerald-400',
    red: 'bg-destructive/15 text-destructive',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider', styles[variant])}>
      {children}
    </span>
  );
}

function statusVariant(status?: string): 'muted' | 'primary' | 'amber' | 'green' | 'red' {
  switch (status) {
    case 'active':
      return 'green';
    case 'paused':
      return 'amber';
    case 'expired':
    case 'cancelled':
      return 'red';
    default:
      return 'muted';
  }
}

function EditMembershipDialog({ row, onClose }: { row: AdminUserRow; onClose: () => void }) {
  const update = useUpdateMembership();
  const setProfile = useAdminSetUserProfile();
  const plans = useMembershipPlans();
  const { toast } = useToast();
  const initial = row.membership;
  const validUntil = tsToDate(initial?.validUntil ?? null);
  const validFrom = tsToDate(initial?.validFrom ?? null);

  const [firstName, setFirstName] = React.useState(row.firstName ?? '');
  const [lastName, setLastName] = React.useState(row.lastName ?? '');
  const [displayName, setDisplayName] = React.useState(row.displayName ?? '');

  const [planId, setPlanId] = React.useState<string>(initial?.planId ?? '');
  const [tier, setTier] = React.useState<'trial' | 'free' | 'pro' | 'enterprise' | 'ultimate'>(initial?.tier ?? 'trial');
  const [status, setStatus] = React.useState<'active' | 'expired' | 'cancelled' | 'paused'>(
    initial?.status ?? 'active',
  );
  const [validFromInput, setValidFromInput] = React.useState(toDateInput(validFrom));
  const [validUntilInput, setValidUntilInput] = React.useState(toDateInput(validUntil));
  const [unlimited, setUnlimited] = React.useState(validUntil == null);
  const [notes, setNotes] = React.useState(initial?.notes ?? '');

  const activePlans = (plans.data ?? []).filter((p) => p.isActive);
  const selectedPlan = activePlans.find((p) => p.id === planId) ?? null;

  function applyPlan(newPlanId: string) {
    setPlanId(newPlanId);
    if (!newPlanId) return;
    const plan = activePlans.find((p) => p.id === newPlanId);
    if (!plan) return;
    setTier(plan.tier);
    if (plan.durationDays != null) {
      const until = new Date();
      until.setDate(until.getDate() + plan.durationDays);
      setValidUntilInput(toDateInput(until));
      setUnlimited(false);
    } else {
      setUnlimited(true);
      setValidUntilInput('');
    }
  }

  function quickExtend(days: number) {
    const base = validUntilInput ? new Date(validUntilInput) : new Date();
    base.setDate(base.getDate() + days);
    setValidUntilInput(toDateInput(base));
    setUnlimited(false);
  }

  async function onSave() {
    try {
      const ops: Promise<unknown>[] = [
        update.mutateAsync({
          uid: row.uid,
          tier,
          status,
          notes,
          planId: planId === '' ? null : planId,
          validFromMs: validFromInput ? new Date(validFromInput).getTime() : undefined,
          validUntilMs: unlimited ? null : (validUntilInput ? new Date(validUntilInput).getTime() : undefined),
        }),
      ];
      const profileChanged =
        firstName.trim() !== (row.firstName ?? '') ||
        lastName.trim() !== (row.lastName ?? '') ||
        displayName.trim() !== (row.displayName ?? '');
      if (profileChanged) {
        ops.push(
          setProfile.mutateAsync({
            uid: row.uid,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            displayName: displayName.trim(),
          }),
        );
      }
      await Promise.all(ops);
      toast({ title: 'Konto aktualisiert' });
      onClose();
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Konto bearbeiten</DialogTitle>
          <DialogDescription>{row.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <section className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Persönliche Daten
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="adm-fn">Vorname</Label>
                <Input
                  id="adm-fn"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={80}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="adm-ln">Nachname</Label>
                <Input
                  id="adm-ln"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={80}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="adm-dn">Anzeigename</Label>
              <Input
                id="adm-dn"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={80}
              />
            </div>
          </section>

          <AdminVerificationDocsSection uid={row.uid} />

          <section className="space-y-3">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Mitgliedschaft
            </h3>
          {activePlans.length > 0 && (
            <div className="space-y-1.5">
              <Label>Plan zuweisen</Label>
              <Select value={planId || '__none__'} onValueChange={(v) => applyPlan(v === '__none__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Manuell konfigurieren" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Manuell konfigurieren (kein Plan)</SelectItem>
                  {activePlans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {p.tier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPlan && (
                <p className="text-xs text-muted-foreground">
                  Tier &amp; Laufzeit werden aus „{selectedPlan.name}" übernommen.
                </p>
              )}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Tier</Label>
              <Select value={tier} onValueChange={(v) => setTier(v as typeof tier)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="ultimate">Ultimate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="paused">Pausiert</SelectItem>
                  <SelectItem value="expired">Abgelaufen</SelectItem>
                  <SelectItem value="cancelled">Gekündigt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="validFrom">Gültig ab</Label>
              <Input
                id="validFrom"
                type="date"
                value={validFromInput}
                onChange={(e) => setValidFromInput(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="validUntil">Gültig bis</Label>
              <Input
                id="validUntil"
                type="date"
                value={validUntilInput}
                onChange={(e) => {
                  setValidUntilInput(e.target.value);
                  if (e.target.value) setUnlimited(false);
                }}
                disabled={unlimited}
              />
              <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={unlimited}
                  onChange={(e) => setUnlimited(e.target.checked)}
                />
                Unbegrenzt
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="self-center text-xs text-muted-foreground">Schnell verlängern:</span>
            <Button type="button" size="sm" variant="outline" onClick={() => quickExtend(7)}>+7 Tage</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => quickExtend(30)}>+30 Tage</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => quickExtend(90)}>+90 Tage</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => quickExtend(365)}>+1 Jahr</Button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notizen</Label>
            <Input
              id="notes"
              value={notes}
              placeholder="z.B. Sondertarif, Vertragsnummer, …"
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={onSave} disabled={update.isPending || setProfile.isPending}>
            {update.isPending || setProfile.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <MoreHorizontal className="mr-2 h-4 w-4" />
            )}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminVerificationDocsSection({ uid }: { uid: string }) {
  const docs = useAdminVerificationDocs(uid);
  const items = docs.data ?? [];
  const labelMap: Record<string, string> = {
    passport: 'Reisepass / Personalausweis',
    companyCert: 'Inhaberschafts-Zertifikat',
    utilityBill: 'Telefon- oder Stromrechnung',
  };
  const ninetyDaysMs = 90 * 86_400_000;
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Verifikations-Dokumente
      </h3>
      {docs.isLoading ? (
        <div className="flex h-12 items-center justify-center text-xs text-muted-foreground">
          <Loader2 className="mr-2 size-3 animate-spin" /> Lade …
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
          Noch keine Dokumente hochgeladen.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((d) => {
            const docDate = tsToDate(d.documentDate);
            const tooOld =
              d.type === 'utilityBill' &&
              docDate &&
              Date.now() - docDate.getTime() > ninetyDaysMs;
            const uploaded = tsToDate(d.uploadedAt);
            return (
              <li
                key={d.type}
                className={cn(
                  'flex flex-wrap items-center gap-2 rounded-md border border-border p-2 text-xs',
                  tooOld && 'border-amber-500/40 bg-amber-500/5',
                )}
              >
                <span className="font-medium">{labelMap[d.type] ?? d.type}</span>
                {d.fileName && (
                  <span className="font-mono text-muted-foreground">{d.fileName}</span>
                )}
                {uploaded && (
                  <span className="text-muted-foreground">
                    · {uploaded.toLocaleDateString('de-DE')}
                  </span>
                )}
                {docDate && (
                  <span className="text-muted-foreground">
                    · Beleg vom {docDate.toLocaleDateString('de-DE')}
                  </span>
                )}
                {tooOld && <Badge variant="amber">&gt; 90 Tage</Badge>}
                {d.signedUrl && (
                  <Button asChild size="sm" variant="ghost" className="ml-auto h-7 text-xs">
                    <a href={d.signedUrl} target="_blank" rel="noreferrer">
                      Ansehen ↗
                    </a>
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function toDateInput(d: Date | null): string {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function prettyName(u: AdminUserRow): string {
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (u.displayName) return u.displayName;
  return '—';
}
